import { useState } from 'react';
import { Button, Card, Empty, Flex, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
	CheckCircleFilled,
	CloseCircleFilled,
	ExclamationCircleFilled,
	PlusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

import {
	VSCPartData,
	VSCPartJson,
} from '../../../interfaces/VersaBuilt.Interface';
import {
	OpenRobotProgram,
	RobotProgramAction,
	RobotProgramActionsButton,
	RobotProgramModals,
} from './RobotProgramActions';
import { newProgramTemplate } from './programTemplate';
import StockDimensionHelp from './StockDimensionHelp';
import UnitSwitch from './UnitSwitch';
import { formatLength } from './units';

const getJsonCreatedByDisplayName = (json: VSCPartJson): string | null => {
	const displayName = json.created_by?.displayName?.trim();
	return displayName || null;
};

const getCreatedDisplay = (record: VSCPartData): string => {
	const jsonAuthor = getJsonCreatedByDisplayName(record.json_data ?? {});
	if (jsonAuthor) {
		return jsonAuthor;
	}
	const user = record.created_by;
	if (user) {
		return user.full_name?.trim() || user.email || '—';
	}
	return '—';
};

const getAddinVersion = (json: VSCPartJson): string | null => {
	const version = json.addin_version;
	if (typeof version !== 'string' || !version.trim()) {
		return null;
	}
	return version.trim();
};

const LENGTH_COLUMNS = [
	{ key: 'partHeight', title: 'Stock Height' },
	{ key: 'width', title: 'Stock Width' },
	{ key: 'length', title: 'Stock Length' },
];

const formatCreatedAt = (createdTime?: string) =>
	createdTime && dayjs(createdTime).isValid()
		? dayjs(createdTime).format('MMM D, YYYY h:mm A')
		: '—';

const getSetupCount = (json: VSCPartJson) =>
	json.op_count ?? (Array.isArray(json.jaws) ? json.jaws.length : null);

interface RobotProgramsProps {
	orderLineItemId: string;
	/** Defaults for a new program's Part Number and Description, as exports use. */
	orderLineItemName?: string;
	orderName?: string;
	programs?: VSCPartData[];
	disabled?: boolean;
}

const RobotPrograms = ({
	orderLineItemId,
	orderLineItemName,
	orderName,
	programs,
	disabled,
}: RobotProgramsProps) => {
	const [openProgram, setOpenProgram] = useState<OpenRobotProgram | null>(null);
	const [useInches, setUseInches] = useState(true);

	const openNewProgram = () =>
		setOpenProgram({
			action: 'create',
			program: {
				id: '',
				json_data: newProgramTemplate(orderLineItemName ?? '', orderName ?? ''),
				created_by: null,
				created_time: '',
				modified_by: null,
				modified_time: '',
			},
			orderLineItemId,
			siblings: programs ?? [],
		});

	const openAction = (record: VSCPartData, action: RobotProgramAction) =>
		setOpenProgram({
			action,
			program: record,
			orderLineItemId,
			siblings: programs ?? [],
		});

	const columns: ColumnsType<VSCPartData> = [
		{
			title: 'Audit',
			width: 64,
			align: 'center',
			render: (_, record) => {
				const issues = record.audit_findings ?? [];
				const hasError = issues.some((issue) => issue.severity === 'error');
				const icon = !issues.length ? (
					<CheckCircleFilled style={{ color: '#389e0d' }} />
				) : hasError ? (
					<CloseCircleFilled style={{ color: '#cf1322' }} />
				) : (
					<ExclamationCircleFilled style={{ color: '#d48806' }} />
				);
				return (
					<Tooltip
						title={
							issues.length ? (
								<ul className="list-disc pl-4 m-0">
									{issues.map((issue) => (
										<li key={issue.field}>{issue.message}</li>
									))}
								</ul>
							) : (
								'Passes the robot program audit'
							)
						}
					>
						<span
							className="cursor-pointer text-base"
							onClick={() => openAction(record, 'view')}
						>
							{icon}
						</span>
					</Tooltip>
				);
			},
		},
		{
			title: 'Name',
			render: (_, record) => record.json_data?.name ?? '—',
		},
		{
			title: 'Description',
			render: (_, record) => record.json_data?.description ?? '—',
		},
		{
			title: 'Setups',
			align: 'right',
			render: (_, record) => getSetupCount(record.json_data) ?? '—',
		},
		...LENGTH_COLUMNS.map(({ key, title }) => ({
			title: (
				<span className="whitespace-nowrap">
					{title}
					<StockDimensionHelp parameterKey={key} />
				</span>
			),
			align: 'right' as const,
			render: (_: unknown, record: VSCPartData) =>
				formatLength(key, record.json_data?.parameters?.[key], useInches),
		})),
		{
			title: 'Created By',
			render: (_, record) => getCreatedDisplay(record),
		},
		{
			title: 'Created At',
			render: (_, record) => formatCreatedAt(record.created_time),
		},
		{
			title: 'Add-in Version',
			render: (_, record) => getAddinVersion(record.json_data ?? {}) ?? '—',
		},
		{
			title: '',
			width: 110,
			render: (_, record) => (
				<RobotProgramActionsButton
					disabled={disabled}
					onAction={(action) => openAction(record, action)}
				/>
			),
		},
	];

	return (
		<Card>
			<Flex
				justify="space-between"
				align="center"
				gap="middle"
				wrap="wrap"
				style={{ marginBottom: 12 }}
			>
				<UnitSwitch useInches={useInches} onChange={setUseInches} />
				<Button
					type="primary"
					icon={<PlusOutlined />}
					disabled={disabled}
					onClick={openNewProgram}
				>
					New Robot Program
				</Button>
			</Flex>
			{programs && programs.length > 0 ? (
				<Table
					rowKey="id"
					size="small"
					dataSource={programs}
					columns={columns}
					pagination={false}
					scroll={{ x: 'max-content' }}
				/>
			) : (
				<Empty description="No Robot Programs Exported" />
			)}

			<RobotProgramModals
				open={openProgram}
				onClose={() => setOpenProgram(null)}
				readOnly={disabled}
				useInches={useInches}
				onChangeUnits={setUseInches}
			/>
		</Card>
	);
};

export default RobotPrograms;
